import { Component, ChangeDetectionStrategy, input, output, inject, signal, computed, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';

@Component({
  selector: 'app-voice-note-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, TranslateModule],
  template: `
    <div class="voice-note-card">
      <div class="voice-note-accent"></div>
      <div class="voice-note-body">
        <!-- Play/Pause Button or Spinner -->
        <button
          type="button"
          class="voice-note-play-btn"
          [class.is-playing]="isPlaying()"
          [disabled]="uploading() || !audioFile()"
          (click)="togglePlayback()"
        >
          @if (uploading()) {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon>{{ isPlaying() ? 'pause' : 'play_arrow' }}</mat-icon>
          }
          <!-- Progress ring -->
          @if (isPlaying() || progress() > 0) {
            <svg class="progress-ring" viewBox="0 0 44 44">
              <circle class="progress-ring-bg" cx="22" cy="22" r="19" />
              <circle
                class="progress-ring-fill"
                cx="22" cy="22" r="19"
                [attr.stroke-dasharray]="circumference"
                [attr.stroke-dashoffset]="circumference - (circumference * progress())"
              />
            </svg>
          }
        </button>

        <div class="voice-note-info">
          <h4 class="voice-note-title">{{ 'VoiceNote.Title' | translate }}</h4>
          <p class="voice-note-status">
            @if (uploading()) {
              {{ 'FileUploader.Uploading' | translate }}...
            } @else if (isPlaying()) {
              {{ formattedCurrentTime() }} / {{ formattedDuration() }}
            } @else if (duration() > 0) {
              {{ formattedDuration() }} · {{ 'VoiceNote.Ready' | translate }}
            } @else {
              {{ 'VoiceNote.Ready' | translate }}
            }
          </p>
        </div>
      </div>
      <button type="button" mat-icon-button class="voice-note-delete" #deleteBtn (click)="onDelete(deleteBtn)">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .voice-note-card {
      display: flex;
      align-items: center;
      position: relative;
      overflow: hidden;
      padding: 12px 12px 12px 0;
      border-radius: 12px;
      background: linear-gradient(135deg, #eef2ff 0%, #f0f4ff 100%);
      border: 1px solid #c7d2fe;
      transition: box-shadow 0.2s ease, transform 0.2s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(90, 115, 197, 0.12);
        transform: translateY(-1px);

        .voice-note-delete {
          opacity: 1;
        }
      }
    }

    .voice-note-accent {
      width: 4px;
      min-height: 100%;
      border-radius: 4px 0 0 4px;
      background: linear-gradient(180deg, var(--skz-primary-color, #5a73c5) 0%, #818cf8 100%);
      margin-right: 12px;
      flex-shrink: 0;
    }

    .voice-note-body {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
    }

    .voice-note-play-btn {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      min-width: 40px;
      border-radius: 10px;
      background: rgba(99, 102, 241, 0.12);
      border: none;
      cursor: pointer;
      transition: background 0.15s ease, transform 0.1s ease;
      padding: 0;

      mat-icon {
        color: var(--skz-primary-color, #5a73c5);
        font-size: 22px;
        width: 22px;
        height: 22px;
        z-index: 1;
      }

      &:hover:not(:disabled) {
        background: rgba(99, 102, 241, 0.2);
        transform: scale(1.05);
      }

      &:active:not(:disabled) {
        transform: scale(0.95);
      }

      &:disabled {
        cursor: default;
        opacity: 0.7;
      }

      &.is-playing {
        background: rgba(99, 102, 241, 0.18);
      }
    }

    .progress-ring {
      position: absolute;
      top: -2px;
      left: -2px;
      width: 44px;
      height: 44px;
      transform: rotate(-90deg);
      pointer-events: none;
    }

    .progress-ring-bg {
      fill: none;
      stroke: rgba(99, 102, 241, 0.1);
      stroke-width: 2.5;
    }

    .progress-ring-fill {
      fill: none;
      stroke: var(--skz-primary-color, #5a73c5);
      stroke-width: 2.5;
      stroke-linecap: round;
      transition: stroke-dashoffset 0.25s ease;
    }

    .voice-note-info {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .voice-note-title {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      color: #312e81;
      line-height: 1.3;
    }

    .voice-note-status {
      margin: 0;
      font-size: 11px;
      font-weight: 500;
      color: #6366f1;
      line-height: 1.3;
    }

    .voice-note-delete {
      opacity: 0;
      transition: opacity 0.15s ease;
      flex-shrink: 0;
      margin-left: auto;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #94a3b8;
      }

      &:hover mat-icon {
        color: var(--skz-text-danger, #c53030);
      }
    }
  `,
})
export class VoiceNoteCardComponent implements OnDestroy {
  uploading = input(false);
  audioFile = input<File | undefined>(undefined);
  removed = output<void>();

  isPlaying = signal(false);
  progress = signal(0);
  duration = signal(0);
  currentTime = signal(0);

  readonly circumference = 2 * Math.PI * 19; // r=19

  formattedDuration = computed(() => this.formatTime(this.duration()));
  formattedCurrentTime = computed(() => this.formatTime(this.currentTime()));

  private confirmMenuService = inject(ConfirmMenuService);
  private audio: HTMLAudioElement | null = null;
  private animationFrameId: number | null = null;
  private blobUrl: string | null = null;

  constructor() {
    // Recreate blob URL when audioFile changes
    effect(() => {
      const file = this.audioFile();
      this.cleanupAudio();
      if (file) {
        this.blobUrl = URL.createObjectURL(file);
        this.audio = new Audio(this.blobUrl);
        this.audio.addEventListener('loadedmetadata', () => {
          this.duration.set(this.audio!.duration);
        });
        this.audio.addEventListener('ended', () => {
          this.isPlaying.set(false);
          this.progress.set(0);
          this.currentTime.set(0);
          this.stopProgressTracking();
        });
      }
    });
  }

  togglePlayback() {
    if (!this.audio) return;

    if (this.isPlaying()) {
      this.audio.pause();
      this.isPlaying.set(false);
      this.stopProgressTracking();
    } else {
      this.audio.play();
      this.isPlaying.set(true);
      this.startProgressTracking();
    }
  }

  onDelete(trigger: MatIconButton) {
    // Stop playback before confirming
    if (this.isPlaying()) {
      this.audio?.pause();
      this.isPlaying.set(false);
      this.stopProgressTracking();
    }
    this.confirmMenuService.confirm(trigger._elementRef, 'VoiceNote.ConfirmDelete', 'delete_outline').subscribe(() => {
      this.removed.emit();
    });
  }

  ngOnDestroy() {
    this.cleanupAudio();
  }

  private startProgressTracking() {
    const update = () => {
      if (this.audio && this.audio.duration) {
        this.currentTime.set(this.audio.currentTime);
        this.progress.set(this.audio.currentTime / this.audio.duration);
      }
      this.animationFrameId = requestAnimationFrame(update);
    };
    update();
  }

  private stopProgressTracking() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private cleanupAudio() {
    this.stopProgressTracking();
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.isPlaying.set(false);
    this.progress.set(0);
    this.currentTime.set(0);
    this.duration.set(0);
  }

  private formatTime(seconds: number): string {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
